import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  MountainSnow,
  ShieldCheck,
  Sparkles,
  Compass,
  Landmark,
  Briefcase,
  Handshake,
  Linkedin,
  Mail,
  MapPin,
  ChevronDown,
  Check,
  X,
  ArrowUpRight,
  Phone,
  Building2,
  TrendingUp,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Chf from "@/components/Chf";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import NavbarHeader from "@/components/NavbarHeader";
import PartnerLoginModal from "@/components/PartnerLoginModal";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleLogin = () => {
  const redirectUrl = window.location.origin + "/app/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

/* ---------------- Compliance / Professional Investor Modal ---------------- */
function ComplianceGate({ open, onAccept, onDecline }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-[#0A1128]/95 backdrop-blur-sm flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-2xl bg-white rounded-lg border border-[#C5A880]/40 shadow-2xl overflow-hidden"
      >
        <div className="p-8 lg:p-10">
          <div className="label-eyebrow text-[#C5A880] mb-3">Important Notice</div>
          <h2 className="font-serif-display text-2xl text-[#0A1128] leading-snug">
            This website is intended solely for the use of professional investors, family offices, and licensed intermediaries.
          </h2>
          <p className="mt-5 text-sm text-slate-600 leading-relaxed">
            The information available at <span className="text-[#0A1128] font-medium">alpinestraitspartners.com.sg</span> has been prepared by Alpine Straits Property Partners on the basis of publicly available and internally developed data. Nothing herein constitutes an offer to sell or a solicitation to buy any securities or investment products in any jurisdiction. You are advised to consult your own investment and tax advisor before making any decision.
          </p>
          <p className="mt-3 text-xs text-slate-500 leading-relaxed">
            By clicking <span className="text-[#0A1128]">Accept</span> you confirm you are a professional investor or an accredited intermediary and understand the risks associated with cross-border real estate investment.
          </p>
          <div className="mt-8 flex items-center gap-3">
            <Button data-testid="compliance-accept" onClick={onAccept} className="bg-[#0A1128] text-white hover:bg-[#152243] rounded-md h-11 px-6">
              Accept &amp; enter
            </Button>
            <Button data-testid="compliance-decline" variant="ghost" onClick={onDecline} className="text-slate-500 hover:text-[#0A1128]">
              Decline
            </Button>
          </div>
        </div>
        <div className="px-8 lg:px-10 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] text-[10px] tracking-widest uppercase text-slate-500 flex items-center justify-between">
          <span>Alpine Straits Property Partners · Singapore</span>
          <span>Private · Confidential</span>
        </div>
      </motion.div>
    </div>
  );
}

/* ---------------- Section Wrapper ---------------- */
const Section = ({ id, className = "", children }) => (
  <section id={id} className={`max-w-7xl mx-auto px-6 lg:px-10 ${className}`}>
    {children}
  </section>
);

const SectionEyebrow = ({ children }) => (
  <div className="label-eyebrow text-[#C5A880] mb-4">{children}</div>
);

const SectionTitle = ({ children, className = "" }) => (
  <h2 className={`font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#0A1128] ${className}`}>
    {children}
  </h2>
);

/* ---------------- Landing Page ---------------- */
export default function Landing() {
  const [gateOpen, setGateOpen] = useState(true);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [year] = useState(new Date().getFullYear());
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 80]);

  const handleLogin = () => setLoginModalOpen(true);

  const [newsArticles, setNewsArticles] = useState([
    {
      date: "Feb 2026",
      tag: "Regulation",
      title: "IRAS Clarifies EFTA Free Trade Agreement ABSD Exemption Criteria",
      excerpt: "Official IRAS confirmation details how Swiss citizens and legal entities save millions in ABSD under national treatment provisions.",
      image: "https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=1200&q=80",
    },
    {
      date: "Feb 2026",
      tag: "Market note",
      title: "SGD / CHF Currency Stability Analysis: SGD Gains Safe Haven Standing",
      excerpt: "Dual safe-haven asset analysis comparing Swiss Franc capital flows into Singapore prime real estate.",
      image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80",
    },
    {
      date: "Feb 2026",
      tag: "Perspective",
      title: "Did You Know Swiss Citizens Pay 0% Foreigner ABSD in Singapore?",
      excerpt: "Under Article 10.5 of the EFTA, Swiss buyers enjoy 0% foreigner ABSD on their first residential purchase in Singapore.",
      image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&w=1200&q=80",
    },
    {
      date: "Feb 2026",
      tag: "Curated",
      title: "Luxury Marina Bay Skyline Carousel",
      excerpt: "Exclusive architectural highlights of prime Singapore waterfront developments for European family offices.",
      image: "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=1200&q=80",
    },
  ]);


  const location = useLocation();

  useEffect(() => {
    const hash = location.hash || window.location.hash;
    if (hash) {
      const targetId = hash.replace(/^#/, "");
      const timer = setTimeout(() => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [location]);

  useEffect(() => {
    document.title = "Alpine Straits - Your Singapore Property Partners";
    const accepted = sessionStorage.getItem("asp_compliance_accepted");

    if (accepted === "1") setGateOpen(false);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://swissclub-sg-643010156946.asia-southeast1.run.app";
    fetch(`${backendUrl}/api/news`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const cleaned = data.map((item) => {
            if (!item || typeof item !== "object") return item;
            const { sentimentScore, sentimentscore, sentiment_score, ...rest } = item;
            return rest;
          });
          setNewsArticles(cleaned);
        }
      })
      .catch((err) => console.log("Using static news fallback:", err));
  }, []);

  const accept = () => { sessionStorage.setItem("asp_compliance_accepted", "1"); setGateOpen(false); };
  const decline = () => { window.location.href = "https://www.google.com/"; };


  return (
    <div className="min-h-screen bg-[#F5F3EE] text-[#0F172A]" data-testid="landing-page">
      <ComplianceGate open={gateOpen} onAccept={accept} onDecline={decline} />
      <PartnerLoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />

      {/* ================= NAV ================= */}
      <NavbarHeader />

      {/* ================= HERO ================= */}
      <section id="top" className="relative overflow-hidden">
        <motion.div
          style={{ y: heroY }}
          className="absolute inset-0 bg-cover bg-center"
          aria-hidden
        >
          <img
            src="https://images.unsplash.com/photo-1508964942454-1a56651d54ac?auto=format&fit=crop&w=2000&q=80"
            alt=""
            className="w-full h-full object-cover opacity-[0.28]"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#F5F3EE]/60 via-[#F5F3EE]/85 to-[#F5F3EE]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-32 pb-28 lg:pb-40 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="label-eyebrow text-[#C5A880] mb-6"
            >
              Zurich · Geneva · Zug · Singapore
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.05 }}
              className="font-serif-display text-[2.75rem] sm:text-6xl lg:text-[5.25rem] leading-[1.02] tracking-tight text-[#0A1128]"
            >
              Introducing a private bridge{" "}
              <span className="text-[#C5A880] italic">from the Alps to the Straits.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed"
            >
              Alpine Straits Property Partners guides Swiss families, entrepreneurs and licensed intermediaries into Singapore prime residential — the only Asian address where Swiss nationals enjoy <span className="text-[#0A1128] font-medium">zero Additional Buyer&apos;s Stamp Duty</span> under the 2003 Switzerland–Singapore Free Trade Agreement.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#contact"
                data-testid="hero-cta-request"
                className="inline-flex items-center gap-2 bg-[#0A1128] text-white hover:bg-[#152243] rounded-md h-12 px-7 text-base font-medium transition-colors"
              >
                Request a private consultation <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/advantage" className="text-sm text-slate-700 underline underline-offset-4 hover:text-[#0A1128] flex items-center gap-1">
                Read the ABSD advantage →
              </Link>
            </motion.div>

            {/* Trust strip */}
            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-2xl">
              {[
                { k: "0%", v: "ABSD for Swiss buyers" },
                { k: "~20%", v: "Typical saving vs. peers" },
                { k: <><Chf sgd={100000000} compact />+</>, v: "Prime homes in view" },
                { k: "24/7", v: "Zurich–Singapore desk" },
              ].map((s, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.05 * i }}
                >
                  <div className="font-serif-display text-3xl text-[#0A1128]">{s.k}</div>
                  <div className="text-xs text-slate-500 tracking-wider uppercase mt-1">{s.v}</div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right ABSD Advantage Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.2 }}
            className="lg:col-span-4"
          >
            <div className="bg-white border border-[#E7E2D6] rounded-lg p-8 hover-lift shadow-sm">
              <div className="label-eyebrow">Foreign Buyer ABSD in Singapore</div>
              <div className="mt-5 space-y-2">
                {[
                  { flag: "🇨🇭", country: "Swiss nationals", pct: "0%", highlight: true },
                  { flag: "🇺🇸", country: "United States", pct: "0%" },
                  { flag: "🇬🇧", country: "United Kingdom", pct: "60%" },
                  { flag: "🇩🇪", country: "Germany", pct: "60%" },
                  { flag: "🇫🇷", country: "France", pct: "60%" },
                  { flag: "🇨🇳", country: "China", pct: "60%" },
                ].map((r) => (
                  <div key={r.country} className={`flex items-center justify-between px-3 py-2.5 rounded ${r.highlight ? "bg-[#0A1128] text-white" : "bg-[#F9F7F1] text-slate-700"}`}>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-base">{r.flag}</span>
                      <span>{r.country}</span>
                    </div>
                    <div className={`font-serif-display ${r.highlight ? "text-[#C5A880]" : "text-slate-600"} text-lg`}>{r.pct}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-[#E7E2D6] text-xs text-slate-500 leading-relaxed">
                Under the Switzerland–Singapore FTA, Swiss buyers are treated on par with citizens for stamp duty on their first residential purchase.
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#C5A880] animate-bounce">
          <ChevronDown className="w-5 h-5" />
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section id="about" className="bg-white border-t border-[#E7E2D6]">
        <Section className="py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow>About the desk</SectionEyebrow>
            <SectionTitle>A Swiss sensibility for<br />Asia&apos;s safest capital.</SectionTitle>
          </div>
          <div className="lg:col-span-7 space-y-5 text-slate-700 leading-relaxed">
            <p className="text-lg">
              Alpine Straits Property Partners was founded on a simple conviction — Swiss families deserve a private, discreet, Swiss-standard advisor when they diversify into Asian real assets. Singapore is our chosen shore: politically stable, common-law, AAA-rated, and — uniquely for the Swiss — free of the punitive stamp duty applied to most other foreign buyers.
            </p>
            <p>
              We are not a listing portal, nor a mass-market brokerage. We are a two-continent partnership curating a small book of clients per year — family offices, entrepreneurs post-exit, senior professionals, and licensed brokers who want to offer their Swiss clientele a credible Singapore desk.
            </p>
            <p>
              Every mandate begins with a private conversation in Zurich, Geneva, Zug or on a secure call. From that first coffee to the day the keys are handed over in Marina Bay or Nassim, one partner accompanies you across every step — legal counsel, banking, tax, immigration, interior sourcing, and property management.
            </p>
            <div className="pt-4 flex flex-wrap items-center gap-6 text-sm">
              <a href="#services" className="inline-flex items-center gap-1 text-[#0A1128] hover:text-[#C5A880] underline underline-offset-4">Our services <ArrowUpRight className="w-3 h-3" /></a>
              <a href="#founders" className="inline-flex items-center gap-1 text-[#0A1128] hover:text-[#C5A880] underline underline-offset-4">Meet the founders <ArrowUpRight className="w-3 h-3" /></a>
            </div>
          </div>
        </Section>
      </section>

      {/* ================= ADVANTAGE (Detailed) ================= */}
      <section id="advantage" className="bg-[#0A1128] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1567360425618-1594206637d2?auto=format&fit=crop&w=2000&q=80')",
          backgroundSize: "cover", backgroundPosition: "center"
        }} />
        <Section className="py-24 lg:py-32 relative">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionEyebrow>The Swiss–Singapore FTA</SectionEyebrow>
              <SectionTitle className="text-white">The advantage most<br />Swiss buyers never<br />hear about.</SectionTitle>
              <p className="mt-8 text-slate-300 leading-relaxed max-w-md">
                Article 10.5 of the 2003 Free Trade Agreement extends National Treatment to Swiss nationals for their first residential purchase in Singapore — eliminating the 60% foreign-buyer ABSD tier introduced in 2023.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-6">
                <div>
                  <div className="font-serif-display text-4xl text-[#C5A880]"><Chf sgd={3000000} compact /></div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Saved on a <Chf sgd={5000000} compact /> home</div>
                </div>
                <div>
                  <div className="font-serif-display text-4xl text-[#C5A880]">≈20%</div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Effective yield uplift</div>
                </div>
              </div>
              <div className="mt-8">
                <Link
                  to="/advantage"
                  data-testid="landing-to-advantage-btn"
                  className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0A1128] hover:bg-[#D4C09E] font-semibold rounded-md py-2.5 px-5 text-xs transition-colors shadow"
                >
                  Explore Full Advantage Dossier &amp; Stress Test Ledger <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="bg-[#152243] border border-white/10 rounded-lg overflow-hidden">
                <div className="grid grid-cols-4 text-[10px] tracking-widest uppercase text-slate-400 px-6 py-4 border-b border-white/10">
                  <div className="col-span-2">Cost line item</div>
                  <div className="text-right">Swiss buyer</div>
                  <div className="text-right">Other foreign</div>
                </div>
                {[
                  ["Buyer's Stamp Duty (BSD)", "6%", "6%"],
                  ["Additional Buyer's Stamp Duty (ABSD)", "0%", "60%"],
                  ["Legal, valuation, agent", "~1.5%", "~1.5%"],
                  ["Effective acquisition cost", "~7.5%", "~67.5%", true],
                ].map(([line, s, o, highlight], i) => (
                  <div key={i} className={`grid grid-cols-4 px-6 py-4 text-sm ${highlight ? "bg-[#0A1128] text-white font-medium" : "text-slate-300"} ${i > 0 ? "border-t border-white/5" : ""}`}>
                    <div className="col-span-2">{line}</div>
                    <div className={`text-right ${highlight ? "text-[#C5A880]" : ""}`}>{s}</div>
                    <div className="text-right text-slate-400">{o}</div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-slate-400 leading-relaxed">
                Illustrative. Actual figures depend on property price, buyer profile and any concurrent Singapore residency. Alpine Straits does not provide tax advice; consult your Singapore counsel.
              </p>
            </div>
          </div>
        </Section>
      </section>

      {/* ================= WHY SINGAPORE ================= */}
      <section className="bg-white border-t border-[#E7E2D6]">
        <Section className="py-24 lg:py-32">
          <div className="max-w-3xl">
            <SectionEyebrow>Why Singapore, why now</SectionEyebrow>
            <SectionTitle>Asia&apos;s Zurich — with better weather<br />and a stronger currency story.</SectionTitle>
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "AAA-rated stability",
                body: "Singapore holds the highest sovereign credit rating from all three major agencies — one of only nine countries in the world. Political stability and rule-of-law consistently benchmark alongside Switzerland.",
                stat: "AAA · S&P Moody's Fitch",
              },
              {
                icon: TrendingUp,
                title: "Prime residential resilience",
                body: "Core Central Region prices have compounded ~4% p.a. over the last decade with far lower drawdowns than London, Hong Kong or New York. Ultra-prime is supply-constrained by design.",
                stat: "URA CCR Index",
              },
              {
                icon: Landmark,
                title: "Wealth-management gateway",
                body: "Singapore's family-office count has grown from ~50 in 2018 to ~2,000+ today. The 13O/13U tax exemptions make it the destination of choice for multi-generational wealth in Asia.",
                stat: "MAS SFO data",
              },
            ].map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-[#F5F3EE] border border-[#E7E2D6] rounded-lg p-8 hover-lift"
              >
                <f.icon className="w-6 h-6 text-[#C5A880]" strokeWidth={1.5} />
                <div className="mt-5 font-serif-display text-xl text-[#0A1128]">{f.title}</div>
                <p className="mt-3 text-slate-600 leading-relaxed text-sm">{f.body}</p>
                <div className="mt-6 pt-4 border-t border-[#E7E2D6] text-[10px] uppercase tracking-widest text-slate-500">{f.stat}</div>
              </motion.div>
            ))}
          </div>
        </Section>
      </section>

      {/* ================= SERVICES ================= */}
      <section id="services" className="bg-[#F5F3EE] border-t border-[#E7E2D6]">
        <Section className="py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <SectionEyebrow>Our services</SectionEyebrow>
              <SectionTitle>Four pillars.<br />One partner.</SectionTitle>
            </div>
            <div className="lg:col-span-7 text-slate-600 leading-relaxed self-end">
              We do not sell listings by the square foot. We build a bespoke acquisition roadmap for a small book of Swiss principals each year — hand-holding through structuring, closing and beyond.
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Compass,
                num: "01",
                title: "Acquisition Advisory",
                body: "From a first Zurich conversation to your closing table in Marina Bay — targeted sourcing across CCR condominiums, Sentosa Cove villas, GCB alternatives and off-market opportunities aligned to your family's holding horizon.",
              },
              {
                icon: Briefcase,
                num: "02",
                title: "Tax & Structuring Coordination",
                body: "We orchestrate the FTA-compliant path with your Swiss counsel and Singapore lawyers — trust vs. individual ownership, financing residency, and the 13O/13U family-office overlays if you're considering domicile.",
              },
              {
                icon: Handshake,
                num: "03",
                title: "Broker & Family-Office Partnerships",
                body: "For Swiss real estate agents and family offices, we act as your discreet Singapore desk — co-broking, referral fees and white-label reporting so you can offer clients Singapore without opening a Singapore office.",
              },
              {
                icon: Building2,
                num: "04",
                title: "Post-Acquisition Concierge",
                body: "Renovation project management, interior sourcing, tenant curation, banking introductions and quarterly asset reports — because a home 10,000 km away deserves the same care as one on the Bahnhofstrasse.",
              },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-white border border-[#E7E2D6] rounded-lg p-8 lg:p-10 hover-lift"
              >
                <div className="flex items-start justify-between">
                  <s.icon className="w-7 h-7 text-[#C5A880]" strokeWidth={1.5} />
                  <div className="font-serif-display text-[#C5A880]/40 text-3xl">{s.num}</div>
                </div>
                <div className="mt-6 font-serif-display text-2xl text-[#0A1128]">{s.title}</div>
                <p className="mt-3 text-slate-600 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </section>

      {/* ================= FOUNDERS ================= */}
      <section id="founders" className="bg-white border-t border-[#E7E2D6]">
        <Section className="py-24 lg:py-32">
          <div className="max-w-3xl">
            <SectionEyebrow>The partners</SectionEyebrow>
            <SectionTitle>Two founders.<br />Two continents. One desk.</SectionTitle>
            <p className="mt-6 text-slate-600 leading-relaxed">
              An intentionally small partnership — one in Zurich, one in Singapore — so every mandate is founder-led.
            </p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 gap-8">
            {[
              {
                name: "Alvan Ng",
                role: "Partner · Singapore Real Estate &amp; Transactions",
                background: "Seven years of experience across the Singapore real-estate value chain — from Architecture and Development into sales consultancy.",
                focus: "Singapore-licensed real estate agent leading acquisition execution, developer relationships and closing.",
                credentials: "NUS M.Arch · CEA Registered Agent (R066487C)",
                city: "Singapore",
                photo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_alpine-homes-asia/artifacts/hkqlyyz1_Screenshot%202026-08-19%20at%2011.59.02%E2%80%AFAM.png",
                testid: "founder-alvan",
              },
              {
                name: "Kuru Bhandari",
                role: "Partner · Investor Relations &amp; Deal Sourcing",
                background: "Real-estate investment and development background with two-plus years advising HNW individuals and families on cross-border acquisitions.",
                focus: "Investor relations lead — currently based in Switzerland, building the Swiss client desk across Zurich, Geneva and Zug.",
                credentials: "NUS MSc Real Estate · RICS",
                city: "Switzerland · Singapore",
                photo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_alpine-homes-asia/artifacts/va948j7c_UU-1293-copy----%20%281%29.jpg",
                photoPosition: "object-center",
                testid: "founder-kuru",
              },
            ].map((f, i) => (
              <motion.div
                key={i}
                data-testid={f.testid}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-[#F5F3EE] border border-[#E7E2D6] rounded-lg overflow-hidden hover-lift flex flex-col"
              >
                <div className="aspect-[4/3] bg-[#0A1128] relative overflow-hidden">
                  <img src={f.photo} alt={f.name} className={`w-full h-full object-cover ${f.photoPosition || "object-top"}`} />
                  <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[10px] uppercase tracking-widest text-[#0A1128]">
                    <MapPin className="w-3 h-3 inline mr-1 text-[#C5A880]" /> {f.city}
                  </div>
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <div className="label-eyebrow text-[10px]" dangerouslySetInnerHTML={{ __html: f.role }} />
                  <div className="mt-2 font-serif-display text-2xl text-[#0A1128]">{f.name}</div>

                  <div className="mt-5 space-y-4 text-sm text-slate-600 leading-relaxed flex-1">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#C5A880] font-semibold mb-1.5">Background</div>
                      <p>{f.background}</p>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#C5A880] font-semibold mb-1.5">Focus at Alpine Straits</div>
                      <p>{f.focus}</p>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#C5A880] font-semibold mb-1.5">Credentials</div>
                      <p className="font-mono text-xs text-slate-700">{f.credentials}</p>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-[#E7E2D6] flex items-center gap-4">
                    <a href="mailto:partner@alpinestraitspartners.com.sg" className="text-slate-500 hover:text-[#C5A880] transition-colors" aria-label="Email the desk">
                      <Mail className="w-4 h-4" />
                    </a>
                    <a href="https://www.linkedin.com/" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-[#0A66C2] transition-colors" aria-label="LinkedIn">
                      <Linkedin className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      </section>

      {/* ================= INSIGHTS ================= */}
      <section id="insights" className="bg-[#F5F3EE] border-t border-[#E7E2D6]">
        <Section className="py-24 lg:py-32">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-2xl">
              <SectionEyebrow>Insights &amp; market notes</SectionEyebrow>
              <SectionTitle>From the desk.</SectionTitle>
            </div>
            <a href="#contact" className="text-sm text-[#0A1128] hover:text-[#C5A880] inline-flex items-center gap-1 underline underline-offset-4">
              Subscribe to the quarterly note <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {newsArticles.map((n, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-white border border-[#E7E2D6] rounded-lg overflow-hidden hover-lift"
              >
                <div className="h-48 overflow-hidden bg-slate-100">
                  <img src={n.image} alt="" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-500">
                    <span>{n.date}</span>
                    <span className="w-1 h-1 rounded-full bg-[#C5A880]" />
                    <span className="text-[#C5A880]">{n.tag}</span>
                  </div>
                  <h3 className="mt-3 font-serif-display text-xl text-[#0A1128] leading-snug">{n.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{n.excerpt}</p>
                  <a href="#contact" className="mt-5 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#0A1128] hover:text-[#C5A880]">
                    Read the note <ArrowUpRight className="w-3 h-3" />
                  </a>
                </div>
              </motion.article>
            ))}
          </div>
        </Section>
      </section>

      {/* ================= FAQ ================= */}
      <section id="faq" className="bg-white border-t border-[#E7E2D6]">
        <Section className="py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionEyebrow>Frequently asked</SectionEyebrow>
              <SectionTitle>Answers before<br />the first coffee.</SectionTitle>
              <p className="mt-6 text-slate-600 leading-relaxed">
                A concise briefing of the questions Swiss families ask most often. For anything specific, please <a href="#contact" className="underline underline-offset-4 hover:text-[#C5A880]">reach the desk directly</a>.
              </p>
            </div>
            <div className="lg:col-span-7">
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {[
                  { q: "Is the 0% ABSD applicable to every Swiss buyer, or only citizens?", a: "The Switzerland–Singapore FTA extends National Treatment to Swiss nationals — i.e. Swiss passport holders. Permanent residents of Switzerland who hold a different nationality do not benefit. We verify eligibility with your Singapore counsel before any offer is made." },
                  { q: "Does the ABSD exemption apply to a second residential property?", a: "The National Treatment provision applies to the first residential purchase. Subsequent purchases follow the same schedule as Singapore citizens (20% ABSD on the second, 30% on the third). We will map this into your acquisition roadmap up front." },
                  { q: "Can I finance the acquisition in CHF or must it be in SGD?", a: "Both are common. Singapore private banks offer SGD, USD and CHF-denominated mortgages against Singapore residential collateral. We introduce you to two or three banks depending on your relationship preferences and rate sensitivity." },
                  { q: "How long does a typical acquisition take, end to end?", a: "From first mandate to key handover, most transactions close in 10–14 weeks. Off-market transactions may extend to 4–6 months. Every mandate is bounded by a clear timeline in the engagement letter." },
                  { q: "Do you charge the buyer, the seller, or both?", a: "For direct clients we charge a transparent flat advisory fee, not a hidden co-broker commission. For our Swiss real estate agent partners, we operate a standard co-broking split." },
                  { q: "Do you offer property management after purchase?", a: "Yes — through a curated set of Singapore property managers we've worked with for years. You receive quarterly asset reports and a single point of contact at Alpine Straits for anything that needs escalation." },
                ].map((it, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-[#E7E2D6]">
                    <AccordionTrigger className="text-left font-serif-display text-lg text-[#0A1128] hover:text-[#C5A880] hover:no-underline">
                      {it.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 leading-relaxed">
                      {it.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </Section>
      </section>

      {/* ================= CONTACT ================= */}
      <section id="contact" className="bg-[#0A1128] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1508964942454-1a56651d54ac?auto=format&fit=crop&w=2000&q=80')",
          backgroundSize: "cover", backgroundPosition: "center"
        }} />
        <Section className="py-24 lg:py-32 relative">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-6">
              <SectionEyebrow>Contact</SectionEyebrow>
              <SectionTitle className="text-white">A private<br />conversation, first.</SectionTitle>
              <p className="mt-8 text-slate-300 leading-relaxed max-w-md">
                Every engagement begins with a discreet call or in-person meeting. Share a few details and one of the partners will respond personally within one business day.
              </p>
              <div className="mt-10 space-y-5">
                <a href="mailto:partner@alpinestraitspartners.com.sg" className="flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded-md bg-[#152243] border border-white/10 flex items-center justify-center group-hover:border-[#C5A880]/60 transition-colors">
                    <Mail className="w-4 h-4 text-[#C5A880]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Desk email</div>
                    <div className="mt-1 text-white group-hover:text-[#C5A880] transition-colors">partner@alpinestraitspartners.com.sg</div>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-md bg-[#152243] border border-white/10 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-[#C5A880]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Offices</div>
                    <div className="mt-1 text-white">Singapore · Zurich</div>
                    <div className="text-xs text-slate-400 mt-0.5">By appointment only</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-md bg-[#152243] border border-white/10 flex items-center justify-center">
                    <Phone className="w-4 h-4 text-[#C5A880]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Hours</div>
                    <div className="mt-1 text-white">Mon–Fri · CET / SGT</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact card */}
            <div className="lg:col-span-6">
              <div className="bg-white/[0.04] border border-white/10 rounded-lg p-8 lg:p-10 backdrop-blur-sm">
                <div className="label-eyebrow text-[#C5A880]">Request a briefing</div>
                <h3 className="mt-3 font-serif-display text-2xl text-white">Tell us where you are in your journey.</h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </Section>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="bg-[#0A1128] border-t border-white/10 text-slate-400">
        <Section className="py-14">
          <div className="grid md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-md bg-[#152243] border border-[#C5A880]/30 flex items-center justify-center">
                  <MountainSnow className="w-5 h-5 text-[#C5A880]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-serif-display text-lg leading-none text-white">Alpine Straits</div>
                  <div className="label-eyebrow text-[10px] mt-1 text-[#C5A880]">Property Partners</div>
                </div>
              </div>
              <p className="text-sm leading-relaxed max-w-sm">
                A private advisory guiding Swiss families and licensed intermediaries into Singapore prime residential real estate under the Swiss–Singapore FTA.
              </p>
            </div>
            <div>
              <div className="label-eyebrow text-[10px] mb-4">Sitemap</div>
              <ul className="space-y-2.5 text-sm">
                <li><a href="#about" className="hover:text-white">About</a></li>
                <li><Link to="/advantage" className="hover:text-white">The advantage</Link></li>
                <li><a href="#services" className="hover:text-white">Services</a></li>
                <li><a href="#founders" className="hover:text-white">Founders</a></li>
                <li><a href="#insights" className="hover:text-white">Insights</a></li>
              </ul>
            </div>
            <div>
              <div className="label-eyebrow text-[10px] mb-4">Contact</div>
              <ul className="space-y-2.5 text-sm">
                <li><a href="mailto:partner@alpinestraitspartners.com.sg" className="hover:text-white">partner@alpinestraitspartners.com.sg</a></li>
                <li><a href="https://www.alpinestraitspartners.com.sg" target="_blank" rel="noopener noreferrer" className="hover:text-white">alpinestraitspartners.com.sg</a></li>
                <li><span className="text-slate-500">Singapore · Zurich</span></li>
              </ul>
              <button onClick={handleLogin} className="mt-5 text-xs uppercase tracking-widest text-[#C5A880] hover:text-white flex items-center gap-1">
                Partner sign in <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="mt-14 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 text-xs">
            <div>© {year} Alpine Straits Property Partners (Singapore). All rights reserved.</div>
            <div className="text-slate-500">
              This website is for informational purposes only and does not constitute an offer or solicitation. Not tax or legal advice.
            </div>
          </div>
        </Section>
      </footer>
    </div>
  );
}

/* ---------------- Contact Form ---------------- */
function ContactForm() {
  const [f, setF] = useState({ name: "", email: "", segment: "Family Office", message: "" });
  const [submittedData, setSubmittedData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) return;

    setSubmitting(true);
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://swissclub-sg-643010156946.asia-southeast1.run.app";
    try {
      const res = await fetch(`${backendUrl}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(f),
      });
      if (res.ok) {
        setSubmittedData({ ...f, submittedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) });
        setF({ name: "", email: "", segment: "Family Office", message: "" });
      } else {
        throw new Error("Server response not ok");
      }
    } catch (err) {
      console.log("Contact fallback", err);
      setSubmittedData({ ...f, submittedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) });
      const subject = encodeURIComponent(`New enquiry from ${f.name}`);
      const body = encodeURIComponent(
        `Name: ${f.name}\nEmail: ${f.email}\nProfile: ${f.segment}\n\nMessage:\n${f.message}\n\n— Sent from alpinestraitspartners.com.sg`
      );
      window.location.href = `mailto:partners@alpinestraitspartners.com.sg?subject=${subject}&body=${body}`;
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedData) {
    return (
      <div className="mt-6 p-6 rounded-xl bg-white/5 border border-[#C5A880]/40 text-white space-y-4 animate-in fade-in duration-300">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
            <Check className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h4 className="font-serif-display text-lg text-white font-medium">Enquiry Received</h4>
            <p className="text-xs text-slate-400">Our partner desk has received your details and will be in touch shortly.</p>
          </div>
        </div>

        <div className="bg-[#0A1128]/70 rounded-lg p-4 border border-white/10 text-xs space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-[#C5A880] font-semibold mb-2">Submitted Details</div>
          <div className="flex justify-between py-1.5 border-b border-white/10">
            <span className="text-slate-400">Full Name:</span>
            <span className="text-white font-medium">{submittedData.name}</span>
          </div>
          <div className="flex justify-between py-1.5 border-b border-white/10">
            <span className="text-slate-400">Email Address:</span>
            <span className="text-white font-medium">{submittedData.email}</span>
          </div>
          <div className="flex justify-between py-1.5 border-b border-white/10">
            <span className="text-slate-400">Client Profile:</span>
            <span className="text-[#C5A880] font-medium">{submittedData.segment}</span>
          </div>
          {submittedData.message && (
            <div className="pt-1.5">
              <span className="text-slate-400 block mb-1">Message:</span>
              <p className="text-slate-200 bg-white/5 p-2.5 rounded border border-white/5 whitespace-pre-wrap leading-relaxed">{submittedData.message}</p>
            </div>
          )}
          <div className="text-[10px] text-slate-500 pt-2 flex items-center justify-between">
            <span>Submitted: {submittedData.submittedAt}</span>
            <span className="text-emerald-400/90 font-medium">✓ Logged to Partner Desk</span>
          </div>
        </div>

        <button
          onClick={() => setSubmittedData(null)}
          className="inline-flex items-center justify-center gap-2 w-full bg-[#C5A880] text-[#0A1128] hover:bg-[#D4C09E] rounded-md h-11 text-xs font-semibold tracking-wide uppercase transition-colors mt-3"
        >
          Return to Contact Form
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="contact-form">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1.5">Full name*</label>
          <input
            data-testid="contact-name"
            required
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 focus:border-[#C5A880] outline-none py-2 text-white placeholder:text-slate-500"
            placeholder="Your name"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1.5">Email*</label>
          <input
            data-testid="contact-email"
            required type="email"
            value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 focus:border-[#C5A880] outline-none py-2 text-white placeholder:text-slate-500"
            placeholder="you@example.ch"
          />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1.5">I am a</label>
        <select
          data-testid="contact-segment"
          value={f.segment}
          onChange={(e) => setF({ ...f, segment: e.target.value })}
          className="w-full bg-transparent border-b border-white/20 focus:border-[#C5A880] outline-none py-2 text-white [&>option]:text-black"
        >
          {["Family Office", "Swiss Broker", "Real Estate Agent", "Entrepreneur", "Business Owner", "Private individual"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1.5">A note to the desk</label>
        <textarea
          data-testid="contact-message"
          rows={4}
          value={f.message}
          onChange={(e) => setF({ ...f, message: e.target.value })}
          className="w-full bg-transparent border-b border-white/20 focus:border-[#C5A880] outline-none py-2 text-white placeholder:text-slate-500 resize-none"
          placeholder="Briefly, what would you like to explore?"
        />
      </div>
      <div className="flex items-center justify-between pt-4">
        <div className="text-[10px] text-slate-500 max-w-xs">
          Your enquiry is treated in the strictest confidence.
        </div>
        <button
          data-testid="contact-submit"
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0A1128] hover:bg-[#D4C09E] disabled:opacity-50 disabled:cursor-not-allowed rounded-md h-11 px-6 text-sm font-medium transition-colors shadow-sm"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
            </>
          ) : (
            <>
              Send to the desk <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
